import React, { useEffect, useState } from 'react';
import styles from './MemoBuilder.module.css';

const STORAGE_KEY = 'memoBuilder.fundThesis';

// PLACEHOLDER: swap in Origin Ventures' actual thesis text.
export const DEFAULT_THESIS = `Origin Ventures invests at the seed and Series A stages in software and technology-enabled businesses with a clear path to category leadership.

We look for:
- Founders with earned insight into the problem and the ability to recruit an exceptional team
- Large or fast-growing markets where software can capture meaningful value
- Early evidence of pull: retention, usage depth, or revenue growth that outpaces spend
- Capital-efficient business models with a credible path to strong unit economics
- Defensibility that compounds over time through data, network effects, or workflow lock-in

We typically lead or co-lead rounds, take a board seat, and reserve for follow-on investment in our strongest companies.`;

function load(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? DEFAULT_THESIS;
  } catch {
    return DEFAULT_THESIS;
  }
}

interface Props {
  thesis: string;
  onChange: (thesis: string) => void;
}

export function useStoredThesis(): [string, (t: string) => void] {
  const [thesis, setThesis] = useState(load);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, thesis);
    } catch {
      // Storage blocked (private mode etc.); the thesis still works for this visit.
    }
  }, [thesis]);
  return [thesis, setThesis];
}

const ThesisEditor: React.FC<Props> = ({ thesis, onChange }) => (
  <div className={styles.stack}>
    <textarea
      className={`${styles.input} ${styles.textarea}`}
      rows={12}
      value={thesis}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Fund thesis"
    />
    <div className={styles.noteMeta}>
      <span className={styles.muted}>Saved in this browser automatically.</span>
      {thesis !== DEFAULT_THESIS && (
        <button type="button" className={styles.linkButton} onClick={() => onChange(DEFAULT_THESIS)}>
          Reset to default
        </button>
      )}
    </div>
  </div>
);

export default ThesisEditor;
